import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  AnchorLookupQuery,
  ProductLookupByNamesQuery,
  WorkspaceProductsQuery,
  type ProductLookupRow,
} from '@graphql/queries/PerceptionModelQueries';

import {
  anchorGgvarMean,
  findAmbiguousAnchors,
  rankCandidates,
  type Candidate,
} from './normSetUtils';

/**
 * The anchors → candidates pipeline, kept out of NormSetBuilder to stop that
 * component sprawling.
 *
 * Two network calls: fetch the anchor rows (which already carry their nearest
 * peers), then fetch lookup rows for the ranked candidates. Ranking itself runs
 * in the browser over a few hundred entries.
 */

type Args = {
  jobId?: number;
  anchorNames: string[];
  topN: number;
  producerId?: number;
  /**
   * Products chosen outside the ranking. Picks made from the map are in the
   * norm set but not in `candidates`, so without them here their profiles are
   * never fetched and they are missing from the set's own sensory signature.
   *
   * Deliberately the custom picks rather than the whole membership: membership
   * is derived from `candidates`, and feeding that back in would hand this hook
   * its own output.
   */
  extraNames?: Set<string>;
};

export type NormSetWorkbench = {
  anchorRows: ProductLookupRow[];
  /** Picked anchors with no lookup row — not in the model, cannot anchor. */
  missingAnchors: string[];
  /** Anchors whose model row was not built from this workspace's product. */
  ambiguousAnchors: string[];
  candidates: Candidate[];
  lookupByName: Map<string, ProductLookupRow>;
  anchorMean: Record<string, number>;
  attributes: string[];
  anchorLoading: boolean;
  candidatesLoading: boolean;
};

const EMPTY_SET: Set<string> = new Set();

const useNormSetWorkbench = ({
  jobId,
  anchorNames,
  topN,
  producerId,
  extraNames = EMPTY_SET,
}: Args): NormSetWorkbench => {
  const { data: anchorData, loading: anchorLoading } = useQuery(
    AnchorLookupQuery,
    {
      variables: { jobId, names: anchorNames },
      skip: !jobId || anchorNames.length === 0,
    },
  );
  const anchorRows: ProductLookupRow[] =
    anchorData?.allPerceptionModelProductLookups?.nodes ?? [];

  const missingAnchors = React.useMemo(
    () =>
      anchorNames.filter((n) => !anchorRows.some((r) => r.productName === n)),
    [anchorNames, anchorRows],
  );

  // Product names are not unique (~1.6k rows share a name with another
  // product), and the model universe is keyed on name. Cross-check the lookup
  // row's product_ids against the workspace's own ids for that name, so an
  // analyst is told when they may be anchoring on someone else's product.
  const { data: wsData } = useQuery(WorkspaceProductsQuery, {
    variables: { producerId },
    skip: !producerId,
  });

  const ambiguousAnchors = React.useMemo(
    () => findAmbiguousAnchors(anchorRows, wsData?.allProducts?.nodes ?? []),
    [wsData, anchorRows],
  );

  const candidates = React.useMemo(
    () => rankCandidates(anchorRows, topN),
    [anchorRows, topN],
  );

  const { data: candData, loading: candidatesLoading } = useQuery(
    ProductLookupByNamesQuery,
    {
      variables: { jobId, names: candidates.map((c) => c.product) },
      skip: !jobId || candidates.length === 0,
    },
  );

  /**
   * Products in the norm set that the ranking never produced — picks made off
   * the map.
   *
   * Fetched separately rather than by widening the query above. Folding them in
   * would change its `names` on every pick, refetching all two hundred ranked
   * profiles to learn about one product.
   */
  const extras = React.useMemo(() => {
    const ranked = new Set(candidates.map((c) => c.product));
    return Array.from(extraNames).filter((n) => !ranked.has(n));
  }, [candidates, extraNames]);

  const { data: extraData } = useQuery(ProductLookupByNamesQuery, {
    variables: { jobId, names: extras },
    skip: !jobId || extras.length === 0,
  });

  const lookupByName = React.useMemo(() => {
    const m = new Map<string, ProductLookupRow>();
    [
      ...(candData?.allPerceptionModelProductLookups?.nodes ?? []),
      ...(extraData?.allPerceptionModelProductLookups?.nodes ?? []),
    ].forEach((r: ProductLookupRow) => m.set(r.productName, r));
    return m;
  }, [candData, extraData]);

  const anchorMean = React.useMemo(
    () => anchorGgvarMean(anchorRows),
    [anchorRows],
  );

  // Stable attribute order shared by every strip, so columns line up.
  const attributes = React.useMemo(
    () => Object.keys(anchorMean).sort(),
    [anchorMean],
  );

  return {
    anchorRows,
    missingAnchors,
    ambiguousAnchors,
    candidates,
    lookupByName,
    anchorMean,
    attributes,
    anchorLoading,
    candidatesLoading,
  };
};

export default useNormSetWorkbench;
